import React, {useContext, useEffect} from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { AuthContext } from '../../context/AuthContext'
import {checkEmailExists, getUser, updateUser} from '../../api/supabaseApi'
import InputPrimary from "../../components/forms/InputPrimary";
import PrimaryButton from "../../components/buttons/PrimaryButton";
import useApi from "../../hooks/useApi";

const step1Schema = yup.object().shape({
  real_email: yup
    .string()
    .required('Email is required')
    .email('Invalid email format'),
})

const Register = () => {
  const {
    session: { user },
  } = useContext(AuthContext)
  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
    trigger,
    getValues,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      real_email: '',
    },
  })
  const navigate = useNavigate()
  const { data: profile, loading: profileLoading } = useApi(
    () => getUser(user.id),
    []
  )

  // Update avatarUrl on profile load
  useEffect(() => {
    if (!profileLoading && profile.real_email) navigate('/')
  }, [profile, profileLoading])

  const validateStep = async () => {
    try {
      await step1Schema.validate(getValues(), { abortEarly: false })
      const email = getValues('real_email')
      const emailExists = await checkEmailExists(user.id, email)

      if (emailExists) {
        setError('real_email', {
          type: 'manual',
          message: 'This email already exists. Please use another email.',
        })
        return false
      }

      return true
    } catch (err) {
      if (err.inner) {
        err.inner.forEach((validationError) => {
          setError(validationError.path, {
            type: 'manual',
            message: validationError.message,
          })
        })
      }
      return false
    }
  }
  const onSubmit = async (data) => {
    const isValid = await validateStep()
    if (isValid) {
      try {
        await updateUser(user.id, { real_email: data.real_email })
        navigate('/')
      } catch (error) {
        console.error(error.message)
      }
    }
  }

  const allFields = watch(['real_email'])
  const isFormFilled = allFields.every((field) => field && field.trim() !== '')

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      transition={{ duration: 0.5 }}
      className="md:container mx-auto md:px-4 min-h-screen md:min-h-full"
    >
      <div className="bg-white p-6 w-full mx-auto rounded-lg shadow max-w-[648px]">
        <form onSubmit={handleSubmit(onSubmit)}>
          <p className="text-3xl text-primary font-bold text-center mb-6">
            Set up your email
          </p>

          <div className="space-y-4">
            <div>
              <label htmlFor="real_email" className="font-bold mb-4">
                Email
              </label>
              <InputPrimary
                type="text"
                id="real_email"
                {...register('real_email', { required: 'Email is required' })}
                placeholder="Please enter your email"
              />
              {errors.real_email && (
                <p className="text-red-500 text-sm">{errors.real_email.message}</p>
              )}
            </div>

            <PrimaryButton
              disabled={!isFormFilled}
              className="w-full"
              type="submit"
            >
              Continue
            </PrimaryButton>
          </div>
        </form>
      </div>
    </motion.div>
  )
}

export default Register
